/**
 * AuthProtected Component
 * 
 * Protects authenticated routes by:
 * 1. Checking authentication status (Redux preferred, sessionStorage fallback)
 * 2. Setting authorization token in axios headers
 * 3. Initializing app data if authenticated
 * 4. Redirecting to login if not authenticated
 * 
 * Loop Prevention:
 * - Pathname check prevents redirects when already on login page
 * - window.location.replace() is idempotent (safe to call multiple times)
 */
import React, { useEffect, useState, useRef } from "react";
import { setAuthorization } from "../helpers/api_helper";
import { useDispatch, useSelector } from "react-redux";
import { logoutUser } from "../slices/auth/login/thunk";
import { appInitService } from "../services/AppInitService";
import { authService } from "../services/authService";
import { setQrReturnTo } from "../helpers/authReturnTo";
import { isAllowedWebProfile } from "../helpers/adminAccess";

const AuthProtected = (props: any) => {
  const dispatch = useDispatch();
  
  // Select only login state to prevent unnecessary re-renders
  const loginState = useSelector((state: any) => state.login);
  const loading = useSelector((state: any) => state.login?.loading || false);
  
  // Track initialization state to prevent duplicate operations
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const hasInitializedRef = useRef(false);
  const hasCheckedAuthRef = useRef(false);
  const isInitializingRef = useRef(false);

  /**
   * Main effect: Handle authentication check and app initialization
   * 
   * Phases:
   * 1. Initial auth check (once when loading completes)
   * 2. App initialization (if authenticated and not already initialized)
   */
  useEffect(() => {
    // Phase 1: Initial auth check (runs once)
    if (!hasCheckedAuthRef.current) {
      if (loading) return; // Wait for Redux to finish loading
      
      const hasAuth = authService.isAuthenticated({ login: loginState });
      if (hasAuth) {
        const token = authService.getToken({ login: loginState });
        if (token) setAuthorization(token);
      }
      
      setIsCheckingAuth(false);
      hasCheckedAuthRef.current = true;
      return;
    }
    
    // Phase 2: App initialization (runs once after auth check)
    if (hasCheckedAuthRef.current && !hasInitializedRef.current) {
      if (isCheckingAuth || loading) return;
      
      const hasAuth = authService.isAuthenticated({ login: loginState });
      
      if (hasAuth && !isInitializingRef.current) {
        const token = authService.getToken({ login: loginState });
        if (token) {
          setAuthorization(token);
          // Initialize app data if not already initialized
          if (!appInitService.getIsInitialized()) {
            isInitializingRef.current = true;
            appInitService.initialize(dispatch).finally(() => {
              isInitializingRef.current = false;
              hasInitializedRef.current = true;
            });
          } else {
            hasInitializedRef.current = true;
          }
        } else {
          hasInitializedRef.current = true;
        }
      } else if (!hasAuth) {
        dispatch(logoutUser() as any);
        hasInitializedRef.current = true;
      }
    }
  }, [loading, isCheckingAuth, loginState, dispatch]);

  // Check authentication status using AuthService
  const hasAnyAuth = authService.isAuthenticated({ login: loginState });
  
  /**
   * Ensure token is always set in axios headers when auth state changes
   * Must run before any early returns (React Hooks rule)
   */
  useEffect(() => {
    if (hasAnyAuth && !isCheckingAuth) {
      const token = authService.getToken({ login: loginState });
      if (token) setAuthorization(token);
    }
  }, [hasAnyAuth, isCheckingAuth, loginState]);

  /**
   * Loop Prevention: Check if already on login page
   * If on login page, return null (let login component handle rendering)
   */
  const isOnLoginPage = window.location.pathname === "/login" ||
                        window.location.pathname === "/otp-verification" ||
                        window.location.pathname === "/field/login" ||
                        window.location.pathname === "/field/otp";

  if (isOnLoginPage) {
    return null;
  }

  /**
   * Admin-only gate for the desktop web app.
   *
   * The field app (/field/*) shares this SPA and the same login endpoint, so a
   * field worker's token also works on desktop routes. This backstops the
   * login-time gate: any authenticated session on a non-field route whose
   * profile is not admin-level is logged out and bounced to /login. Field
   * routes are intentionally exempt. (See helpers/adminAccess.)
   */
  const isFieldRoute = window.location.pathname.startsWith("/field");
  if (!isCheckingAuth && !loading && hasAnyAuth && !isFieldRoute) {
    const currentUser = authService.getUser({ login: loginState });
    if (currentUser && !isAllowedWebProfile(currentUser.profileName)) {
      try {
        sessionStorage.removeItem("persist:root");
      } catch (e) {
        // Ignore storage errors (may not be available in some environments)
      }
      dispatch(logoutUser() as any);
      window.location.replace("/login?denied=admins-only");
      return null;
    }
  }

  /**
   * Redirect to login if no authentication found
   * 
   * Loop prevention:
   * - Pathname check above ensures we're NOT already on login page
   * - window.location.replace() is idempotent (safe to call multiple times)
   * 
   * @returns null if redirecting, otherwise renders children
   */
  if (!isCheckingAuth && !hasAnyAuth && !loading) {
    // Double-check auth status using AuthService (handles Redux + sessionStorage)
    const finalAuthCheck = authService.isAuthenticated({ login: loginState });
    
    // If auth found, ensure token is set in axios headers and continue
    if (finalAuthCheck) {
      const authToken = authService.getToken({ login: loginState });
      if (authToken) {
        setAuthorization(authToken);
      }
      return <>{props.children}</>;
    }
    
    // No auth found - redirect to login
    
    // Clear storage and redirect to login
    try {
      sessionStorage.removeItem('persist:root');
    } catch (e) {
      // Ignore storage errors (may not be available in some environments)
    }
    
    dispatch(logoutUser() as any);
    // Field app routes → mobile login, everything else → desktop login
    // (isFieldRoute is computed once at the top of the component).
    // Remember a scaffold QR deep-link so the user returns to it after login
    // (no-op for any non-/qr/ path — normal login flow is untouched).
    setQrReturnTo(window.location.pathname + window.location.search);
    window.location.replace(isFieldRoute ? "/field/login" : "/login");
    return null;
  }

  return <>{props.children}</>;
};

export default AuthProtected;

// import React, { useEffect } from "react";
// import { Navigate, Route } from "react-router-dom";
// import { setAuthorization } from "../helpers/api_helper";
// import { useDispatch } from "react-redux";

// import { useProfile } from "../Components/Hooks/UserHooks";

// import { logoutUser } from "../slices/auth/login/thunk";

// const AuthProtected = (props  : any) => {
//   const dispatch  : any = useDispatch();
//   const { userProfile, loading, token } = useProfile();
//   useEffect(() => {
//     if (userProfile && !loading && token) {
//       setAuthorization(token);
//     } else if (!userProfile && loading && !token) {
//       dispatch(logoutUser());
//     }
//   }, [token, userProfile, loading, dispatch]);

//   /*
//     Navigate is un-auth access protected routes via url
//     */

//   if (!userProfile && loading && !token) {
//     return (
//       <Navigate to={{ pathname: "/login", state: { from: props.location } }} />
//     );
//   }

//   return <>{props.children}</>;
// };

// const AccessRoute = ({ component: Component, ...rest }) => {
//   return (
//     <Route
//       {...rest}
//       render={(props  : any) => {
//         return (<> <Component {...props} /> </>);
//       }}
//     />
//   );
// };

// export { AuthProtected, AccessRoute };
